import Vue from "vue";
import {
  CallReceptionState,
  CallsApi,
  ConnectedDevicesResponse,
  DaktelaDeviceModel,
  IncomingCallAnswered,
  SettingsApi,
} from "@/api";
import { getDefaultApiConfig } from "@/utils";
import { eventBus, Events } from "@/utils/event-bus";
import { isDaktelaEnabled } from "@/store/daktela";

interface DeviceKeyParts {
  name?: string | null;
  model?: string | null;
}

export type DaktelaEmailQueueState =
  | "unknown"
  | "configured"
  | "notConfigured"
  | "unavailable";

export interface OutgoingCall {
  phoneNumber: string | null;
  activityName: string | null;
  startedAt: number;
}

interface CallReceptionStore {
  state: CallReceptionState | null;
  loadingState: boolean;
  devices: DaktelaDeviceModel[];
  loadingDevices: boolean;
  connectedDeviceKeys: string[];
  selectedDeviceKeys: string[];
  updatingDevices: boolean;
  activeCall: IncomingCallAnswered | null;
  outgoingCall: OutgoingCall | null;
  emailQueue: DaktelaEmailQueueState;
}

const store = Vue.observable<CallReceptionStore>({
  state: null,
  loadingState: false,
  devices: [],
  loadingDevices: false,
  connectedDeviceKeys: [],
  selectedDeviceKeys: [],
  updatingDevices: false,
  activeCall: null,
  outgoingCall: null,
  emailQueue: "unknown",
});

const PollIntervalMs = 15000;
const OutgoingCallTtlMs = 900000;
const OutgoingCallGuardMs = 30000;

let settingsApiInstance: SettingsApi | null = null;
let callsApiInstance: CallsApi | null = null;

function settingsApi(): SettingsApi {
  if (!settingsApiInstance) {
    settingsApiInstance = new SettingsApi(getDefaultApiConfig());
  }
  return settingsApiInstance;
}

function callsApi(): CallsApi {
  if (!callsApiInstance) {
    callsApiInstance = new CallsApi(getDefaultApiConfig());
  }
  return callsApiInstance;
}

let subscribers = 0;
let pollHandle: number | null = null;
let updatesInProgress = 0;
let loadingState = false;
let loadingDevices = false;
let loadingConnectedDevices = false;
let loadingActiveCall = false;
let loadingEmailQueue = false;
let pendingLocalDevicePick = false;
let activeCallSetAt = 0;

export function getCallReceptionState(): CallReceptionState | null {
  return store.state;
}

export function setCallReceptionState(state: CallReceptionState | null): void {
  store.state = state;
}

export function isCallReceptionStateLoading(): boolean {
  return store.loadingState;
}

export function getAvailableDevices(): DaktelaDeviceModel[] {
  return store.devices;
}

export function areAvailableDevicesLoading(): boolean {
  return store.loadingDevices;
}

function setConnectedDeviceKeys(keys: string[]): void {
  store.connectedDeviceKeys = keys;
  syncSelectedDeviceKeys();
}

export function getSelectedDeviceKeys(): string[] {
  return store.selectedDeviceKeys;
}

export function areConnectedDevicesUpdating(): boolean {
  return store.updatingDevices;
}

export function hasConnectedDevice(): boolean {
  return store.connectedDeviceKeys.length > 0;
}

export function deviceKey(device: DeviceKeyParts): string {
  return `${device.model}|${device.name}`;
}

function connectedDeviceKeysFrom(response: ConnectedDevicesResponse): string[] {
  return (response.devices ?? [])
    .filter((d) => d.name && d.model)
    .map((d) => deviceKey(d));
}

export function deviceFor(key: string): DaktelaDeviceModel | undefined {
  return store.devices.find((d) => deviceKey(d) === key);
}

export function getActiveCall(): IncomingCallAnswered | null {
  return store.activeCall;
}

export function setActiveCall(call: IncomingCallAnswered | null): void {
  store.activeCall = call;
  activeCallSetAt = Date.now();
}

export function getOutgoingCall(): OutgoingCall | null {
  return store.outgoingCall;
}

export function setOutgoingCall(call: {
  phoneNumber?: string | null;
  activityName?: string | null;
}): void {
  store.outgoingCall = {
    phoneNumber: call.phoneNumber ?? null,
    activityName: call.activityName ?? null,
    startedAt: Date.now(),
  };
}

export function clearOutgoingCall(): void {
  store.outgoingCall = null;
}

export function hasActiveCall(): boolean {
  return store.activeCall !== null || isOutgoingCallWithinGuard();
}

export function isCallQueueHidden(): boolean {
  return store.state === CallReceptionState.NotReceiving;
}

export function getDaktelaEmailQueueState(): DaktelaEmailQueueState {
  return store.emailQueue;
}

export function beginCallReceptionUpdate(): void {
  updatesInProgress += 1;
}

export function endCallReceptionUpdate(): void {
  updatesInProgress = Math.max(0, updatesInProgress - 1);
}

export function subscribeCallReception(): void {
  subscribers += 1;

  if (subscribers === 1) {
    eventBus.$on(Events.CallReceptionConfigChanged, refreshCallReception);
    pollHandle = window.setInterval(pollCallReception, PollIntervalMs);
  }

  refreshCallReception();
}

function resetCallReception(): void {
  store.state = null;
  store.loadingState = false;
  store.devices = [];
  store.connectedDeviceKeys = [];
  store.selectedDeviceKeys = [];
  store.loadingDevices = false;
  store.updatingDevices = false;
  store.activeCall = null;
  store.outgoingCall = null;
  store.emailQueue = "unknown";
}

export function unsubscribeCallReception(): void {
  subscribers = Math.max(0, subscribers - 1);

  if (subscribers > 0) {
    return;
  }

  eventBus.$off(Events.CallReceptionConfigChanged, refreshCallReception);

  if (pollHandle !== null) {
    window.clearInterval(pollHandle);
    pollHandle = null;
  }
}

function refreshCallReception(): void {
  if (!isDaktelaEnabled()) {
    resetCallReception();
    return;
  }

  refreshCallReceptionState(store.state !== null);
  refreshConnectedDevices();
  refreshMyActiveCall();
  refreshDaktelaEmailQueue();
}

export async function refreshCallReceptionState(silent = false): Promise<void> {
  if (loadingState) {
    return;
  }

  loadingState = true;
  if (!silent) {
    store.loadingState = true;
  }

  try {
    const response = await settingsApi().getCallReception();
    store.state = response.data.state ?? CallReceptionState.Unavailable;
  } catch {
    store.state = CallReceptionState.Unavailable;
  } finally {
    loadingState = false;
    store.loadingState = false;
  }

  if (
    store.devices.length === 0 &&
    (store.state === CallReceptionState.Receiving ||
      store.state === CallReceptionState.NotReceiving)
  ) {
    await refreshAvailableDevices();
  }
}

async function refreshAvailableDevices(): Promise<void> {
  if (loadingDevices) {
    return;
  }

  loadingDevices = true;
  store.loadingDevices = true;

  try {
    const response = await settingsApi().getDaktelaDevices();
    store.devices = response.data.devices ?? [];
  } catch {
    store.devices = [];
  } finally {
    loadingDevices = false;
    store.loadingDevices = false;
  }
}

export async function refreshConnectedDevices(): Promise<void> {
  if (loadingConnectedDevices) {
    return;
  }

  loadingConnectedDevices = true;

  try {
    const response = await settingsApi().getConnectedDevices();
    store.connectedDeviceKeys = connectedDeviceKeysFrom(response.data);
  } catch {
    store.connectedDeviceKeys = [];
  } finally {
    loadingConnectedDevices = false;
    syncSelectedDeviceKeys();
  }
}

export async function refreshMyActiveCall(): Promise<void> {
  if (loadingActiveCall) {
    return;
  }

  loadingActiveCall = true;

  try {
    const response = await callsApi().getMyActiveCall();
    const call = response.data;

    if (call && call.callId) {
      setActiveCall(call);
      return;
    }

    if (
      store.activeCall !== null &&
      Date.now() - activeCallSetAt > PollIntervalMs
    ) {
      setActiveCall(null);
    }
  } catch {
    return;
  } finally {
    loadingActiveCall = false;
  }
}

async function refreshDaktelaEmailQueue(): Promise<void> {
  if (loadingEmailQueue) {
    return;
  }

  if (
    store.emailQueue === "configured" ||
    store.emailQueue === "notConfigured"
  ) {
    return;
  }

  loadingEmailQueue = true;

  try {
    const response = await settingsApi().getDaktelaEmail();
    store.emailQueue = response.data.queueConfigured
      ? "configured"
      : "notConfigured";
  } catch {
    store.emailQueue = "unavailable";
  } finally {
    loadingEmailQueue = false;
  }
}

export async function applyConnectedDeviceSelection(
  newKeys: string[]
): Promise<void> {
  if (store.updatingDevices) {
    return;
  }

  if (store.connectedDeviceKeys.length === 0) {
    store.selectedDeviceKeys = newKeys.slice(-1);
    pendingLocalDevicePick = store.selectedDeviceKeys.length > 0;
    return;
  }

  const added = newKeys.filter((k) => !store.connectedDeviceKeys.includes(k));
  const removed = store.connectedDeviceKeys.filter((k) => !newKeys.includes(k));

  if (
    store.state === CallReceptionState.Receiving &&
    removed.length > 0 &&
    newKeys.length === 0
  ) {
    store.selectedDeviceKeys = [...store.connectedDeviceKeys];
    return;
  }

  store.updatingDevices = true;
  beginCallReceptionUpdate();

  try {
    let response: ConnectedDevicesResponse | null = null;

    for (const key of added) {
      const device = deviceFor(key);
      if (device) {
        response = (
          await settingsApi().connectDevice({
            extension: device.name,
            model: device.model,
          })
        ).data;
      }
    }

    for (const key of removed) {
      const device = deviceFor(key);
      if (device) {
        response = (
          await settingsApi().disconnectDevice({
            extension: device.name,
            model: device.model,
          })
        ).data;
      }
    }

    if (response) {
      pendingLocalDevicePick = false;
      setConnectedDeviceKeys(connectedDeviceKeysFrom(response));
    } else {
      pendingLocalDevicePick = false;
      await refreshConnectedDevices();
    }
  } catch (error) {
    pendingLocalDevicePick = false;
    await refreshConnectedDevices();
    throw error;
  } finally {
    store.updatingDevices = false;
    endCallReceptionUpdate();
  }
}

function isOutgoingCallLive(): boolean {
  const outgoing = store.outgoingCall;
  return (
    outgoing !== null && Date.now() - outgoing.startedAt <= OutgoingCallTtlMs
  );
}

function isOutgoingCallWithinGuard(): boolean {
  const outgoing = store.outgoingCall;
  return (
    outgoing !== null && Date.now() - outgoing.startedAt <= OutgoingCallGuardMs
  );
}

function syncSelectedDeviceKeys(): void {
  if (store.connectedDeviceKeys.length > 0) {
    store.selectedDeviceKeys = [...store.connectedDeviceKeys];
    pendingLocalDevicePick = false;
    return;
  }

  if (!pendingLocalDevicePick) {
    store.selectedDeviceKeys = [];
  }
}

function pollCallReception(): void {
  if (!isDaktelaEnabled()) {
    return;
  }

  if (!isOutgoingCallLive()) {
    clearOutgoingCall();
  }

  if (updatesInProgress > 0) {
    return;
  }

  refreshCallReceptionState(true);
  refreshConnectedDevices();
  refreshMyActiveCall();
  refreshDaktelaEmailQueue();
}
